import {useContext, useState} from 'react'

import {parseQueryString, toQueryString} from '@s-ui/js/lib/string'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {LoginModalContext} from '@adv-mt/mt-context-login-modal'

export const PAGE_ONE = 1

export const DEFAULT_CONFIGURATOR_IMAGE_URL = 'https://s.ccdn.es/images/empty-cases/empty-case-new-vehicles-coches.png'

export const getMakesWithModelsOptions = ({makesWithModels = []}) =>
  makesWithModels.map(({make, models = []}) => ({
    id: make.id,
    label: make.name,
    models: models.map(({id, name}) => ({id, label: name}))
  }))

// The backend identifies excluded vehicles by id only, the tags and the filter need their names
export const addNamesToExcludedVehicles = ({excludedVehicles = [], makesOptions = []}) =>
  excludedVehicles.map(excludedVehicle => {
    const makeOption = makesOptions.find(({id}) => String(id) === String(excludedVehicle.makeId))
    const modelOption = makeOption?.models.find(({id}) => String(id) === String(excludedVehicle.model?.id))

    return {
      ...excludedVehicle,
      make: excludedVehicle.make || makeOption?.label,
      model: {...excludedVehicle.model, name: excludedVehicle.model?.name || modelOption?.label}
    }
  })

export const scrollIntoViewVisitedAd = ({adId}) => {
  const attribute = `[data-ad-id='${adId}']`
  setTimeout(() => {
    const element = document.querySelector(attribute)
    element && element.scrollIntoView({block: 'center'})
  }, 500)
}

const createWithoutPagination = ({urlPattern}) => {
  const [pathName, queryString] = urlPattern.split('?')
  const query = parseQueryString(queryString)

  delete query.pg

  const queryStringWithoutPagination = toQueryString(query, {
    encode: false
  })

  const nextQueryString = queryStringWithoutPagination ? `?${queryStringWithoutPagination}` : ''

  return `${pathName}${nextQueryString}`
}

export const createPaginationUrlFactory =
  pageNumberPlaceholder =>
  ({pageNumber, urlPattern}) => {
    // SEO requires that first page does not have pg=1
    if (pageNumber === PAGE_ONE) {
      return createWithoutPagination({urlPattern})
    }

    return urlPattern.replace(pageNumberPlaceholder, pageNumber)
  }

export const mergeFavoritesResults = ({vehicles = [], favorites = []}) => {
  const hasFavorites = Boolean(favorites.length)

  if (!hasFavorites) return vehicles

  return vehicles.map(item => ({
    ...item,
    isFavorite: hasFavorites && favorites.some(element => Number(element.id) === Number(item.id))
  }))
}

export const getLastSearchTitle = ({categoryId, i18n, offerTypeId, searchConfig, tags}) => {
  const tagsText = tags.map(({label}) => label).join(', ')

  const {
    MOTOS_CATEGORY1_ID,
    QUADS_CATEGORY1_ID,
    BUGGIES_CATEGORY1_ID,
    MINIMOTOS_CATEGORY1_ID,
    CLASSICS_CATEGORY1_ID,
    SEGUNDA_MANO_CATEGORY1_ID,
    DEFAULT_OFFER_TYPE_ID,
    VANS_AND_COMMERCIAL_CATEGORY1_ID,
    AUTOCARAVANS_AND_TRAILERS_CATEGORY1_ID,
    CARS_WITHOUT_LICENSE_CATEGORY1_ID,
    CLASSIC_AND_COMPETITION_CATEGORY1_ID,
    UNSELECTED_OFFER_TYPE
  } = searchConfig

  switch (categoryId) {
    case MOTOS_CATEGORY1_ID:
      return `${i18n.t('SEARCH.MOTOS')} ${tagsText}`
    case QUADS_CATEGORY1_ID:
      return `${i18n.t('SEARCH.QUADS')} ${tagsText}`
    case BUGGIES_CATEGORY1_ID:
      return `${i18n.t('SEARCH.BUGGIES')} ${tagsText}`
    case MINIMOTOS_CATEGORY1_ID:
      return `${i18n.t('SEARCH.MINIMOTOS')} ${tagsText}`
    case CLASSICS_CATEGORY1_ID:
      return `${i18n.t('SEARCH.MOTOS_CLASSIC_AND_COMPETITION')} ${tagsText}`
    case SEGUNDA_MANO_CATEGORY1_ID: {
      if (offerTypeId === UNSELECTED_OFFER_TYPE) {
        return tagsText
      }
      const title = offerTypeId === DEFAULT_OFFER_TYPE_ID ? i18n.t('SEARCH.SECOND_HAND') : i18n.t('SEARCH.KM0')
      return `${title} ${tagsText}`
    }
    case VANS_AND_COMMERCIAL_CATEGORY1_ID:
      return `${i18n.t('SEARCH.VANS_AND_COMMERCIAL')} ${tagsText}`
    case CLASSIC_AND_COMPETITION_CATEGORY1_ID:
      return `${i18n.t('SEARCH.CLASSIC_AND_COMPETITION')} ${tagsText}`
    case AUTOCARAVANS_AND_TRAILERS_CATEGORY1_ID:
      return `${i18n.t('SEARCH.AUTOCARAVANS_AND_TRAILERS')} ${tagsText}`
    case CARS_WITHOUT_LICENSE_CATEGORY1_ID:
      return `${i18n.t('SEARCH.CARS_WITHOUT_LICENSE')} ${tagsText}`
    default:
      return tagsText
  }
}

export const getSearchFaqsTitle = ({semanticDescriptor, i18n}) => {
  return i18n.t('SEARCH_FAQS.TITLE', {
    semanticDescriptor
  })
}

export const useAddFavorite = () => {
  const [isLoginFlowCancelled, setIsLoginFlowCancelled] = useState(false)

  const {domain} = useContext(Context)
  const {triggerLoginModal} = useContext(LoginModalContext)

  const config = domain.get('config')
  const {USER_NOT_LOGGED_IN_USER_ERROR} = config.get('ERRORS')
  const {LOGIN_SOURCE_ID_ADD_FAV} = config.get('login')
  const {HIT_ORIGINS} = config.get('tracking')

  useMount(() => {
    const onNext = () => {}
    const onError = ({params, error}) => {
      const [{adId}] = params
      const userIsNotLoggedIn = error instanceof USER_NOT_LOGGED_IN_USER_ERROR

      userIsNotLoggedIn &&
        triggerLoginModal({
          modalProps: {
            source: LOGIN_SOURCE_ID_ADD_FAV,
            trackingOrigin: HIT_ORIGINS.AD_FAVORITED,
            onLogin: () => {
              const addFavorite = async () => {
                await domain.get('add_user_favorites_use_case').execute({
                  adId
                })
              }
              addFavorite()
            },
            onClose: () => setIsLoginFlowCancelled(true)
          }
        })
    }

    const _addUserFavorite$ = domain.get('add_user_favorites_use_case').$.execute.subscribe(onNext, onError)

    return () => _addUserFavorite$.dispose()
  })

  return isLoginFlowCancelled
}

export const getPageUrlFAQsInfo = ({i18n, faqsForUrlError, faqsForUrlResponse, path}) => {
  const isPageWithFAQs = !faqsForUrlError && faqsForUrlResponse && faqsForUrlResponse.toJSON()?.faqs?.items.length > 0

  const _getFaqsTitleFromPath = ({path}) => {
    if (!isPageWithFAQs) return ''

    const segments = path.split('/').filter(segment => segment && segment.length > 0)

    if (segments.length === 0) return ''

    const splitPath = segments.map(segment => {
      return segment
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ')
    })

    return splitPath && splitPath.length ? i18n.t('COMMON.FAQ_TITLE_PREFIX', {section: splitPath.join(', ')}) : ''
  }
  const faqsForUrlSectionTitle = _getFaqsTitleFromPath({path, faqsForUrlError, faqsForUrlResponse})
  const faqsForUrlItems = isPageWithFAQs ? faqsForUrlResponse.toJSON().faqs.items : []

  return {isPageWithFAQs, faqsForUrlSectionTitle, faqsForUrlItems}
}
